import {
  DEVISE_PAR_DEFAUT,
  NOTE_MAX_PAR_DEFAUT,
  avancementChecklist,
  deviseDe as deviseDuNombre,
  fuseauDe,
  instantDeSac,
  jourDansFuseau,
  formatDuNombre,
  lirePeriode,
  libelleDureeDePeriode,
  seSommeAuCentime,
  sommeAuCentime,
  valeurNormalisee,
} from '@taki/data-model';
import type { ChampAFormat, FormatDeNombre } from '@taki/data-model';
import type { Cell, EditorKind, ReglagesDeColonne } from './contract.js';
import type { TypeDeChamp } from './fields.js';
import { datePure, duree, montant, pourcentage, taux } from './habillages.js';
import { formatDeDate, formatDeNombre as formatIntlDeNombre, nombreEnTexte } from '../lib/formats-intl.js';

/**
 * LES TYPES DE CHAMP COTE INTERFACE : ce que l'ecran fait d'une valeur selon
 * le type de son champ (`docs/socle/champs-types.md`).
 *
 * CE QUE CE MODULE EST. Les regles PURES (ni React, ni contexte) que la fiche,
 * le tableau, le pied de colonne, les stats, les filtres et la fenetre de lot
 * partagent pour les types du 27/10/2026 et les ameliorations de l'existant :
 * lire un Oui / Non stocke en chaine, decouper une liste qui circule une ligne
 * par element, ecrire un nombre avec son unite ou son format, sommer des
 * montants au centime.
 * Chacune est ecrite UNE fois : la fiche qui ecrit « 240 m² » et le pied qui
 * ecrit « 1 250 m² » lisent la meme fonction, sinon ils divergeraient au
 * premier reglage.
 *
 * CE QU'IL N'EST PAS : une seconde lecture des valeurs stockees. La forme d'une
 * periode, d'une liste de controle, d'une duree saisie et la somme au centime
 * vivent dans `@taki/data-model` (`types-de-champ.ts`), partagees avec le
 * serveur ; ce module les APPELLE, il ne les recopie pas.
 */

/* ------------------------------------------------------------------ */
/* Les listes                                                           */
/* ------------------------------------------------------------------ */

/**
 * LES EDITEURS QUI SAISISSENT UNE LISTE. Leur valeur circule dans le moteur en
 * CELLULE, un element par ligne (la lecture du socle rend une liste ainsi,
 * `cellule` dans `app-runtime/src/acces.ts`), et c'est la colonne qui la
 * retraduit en TABLEAU au moment d'ecrire (`colonnesDe`). Le brouillon et le
 * mode Modeles la rangent en tableau pour la meme raison (`valeursPourModele`).
 * `liens` est l'editeur d'un `lien` qui porte `plusieurs`.
 */
const EDITEURS_EN_LISTE: ReadonlySet<EditorKind> = new Set<EditorKind>(['multiselect', 'etiquettes', 'liens', 'checklist']);

/** Cet editeur saisit-il une liste (ecrite en tableau, vide comprise) ? */
export function editeurEnListe(editeur: EditorKind | undefined): boolean {
  return editeur !== undefined && EDITEURS_EN_LISTE.has(editeur);
}

/** CETTE COLONNE PORTE-T-ELLE DES LIENS MULTIPLES ? Un `lien` qui declare `plusieurs`. */
export function liensMultiples(type: TypeDeChamp | undefined, reglages: ReglagesDeColonne | undefined): boolean {
  return type === 'lien' && reglages?.plusieurs === true;
}

/**
 * LES ELEMENTS D'UNE LISTE, lus dans une cellule : UNE LIGNE PAR ELEMENT, et
 * rien d'autre. La virgule n'y coupe pas, a la difference des clefs d'un choix
 * multiple (`clefsDuChoixMultiple`) : une etiquette « Bail, commercial » ou un
 * element de liste de controle « Devis signe, date » sont UN element. Vide,
 * `null` ou blanc : aucun element.
 */
export function lignesDeCellule(valeur: Cell | undefined): string[] {
  if (valeur === null || valeur === undefined || valeur === '') return [];
  return String(valeur)
    .split(/\r?\n/)
    .map((ligne) => ligne.trim())
    .filter((ligne) => ligne !== '');
}

/** Des elements, sans doublon ni blanc, dans l'ordre de leur premiere apparition. */
export function sansDoublon(elements: readonly string[]): string[] {
  const vus: string[] = [];
  for (const element of elements) {
    const texte = element.trim();
    if (texte !== '' && !vus.includes(texte)) vus.push(texte);
  }
  return vus;
}

/** La cellule d'une liste : ses elements un par ligne, `null` quand il n'y en a aucun. */
export function celluleDeListe(elements: readonly string[]): Cell {
  return elements.length === 0 ? null : elements.join('\n');
}

/* ------------------------------------------------------------------ */
/* Le Oui / Non                                                         */
/* ------------------------------------------------------------------ */

/**
 * UN OUI / NON LU, quelle que soit sa forme stockee : le booleen, ou les
 * chaines `"true"` / `"false"` d'avant l'interrupteur (le Oui / Non etait un
 * choix a deux options, et certaines fiches l'ont garde en chaine). Une
 * donnee deja saisie continue de se lire, et le prochain clic l'ecrit en
 * booleen. `null` pour une case vide ou illisible.
 */
export function lireBooleen(valeur: Cell | undefined): boolean | null {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  if (typeof valeur === 'boolean') return valeur;
  if (valeur instanceof Date) return null;
  const lu = valeurNormalisee({ key: '', type: 'boolean' }, valeur);
  return 'valeur' in lu && typeof lu.valeur === 'boolean' ? lu.valeur : null;
}

/* ------------------------------------------------------------------ */
/* Les nombres                                                          */
/* ------------------------------------------------------------------ */

/**
 * UN NOMBRE A LA FRANCAISE, avec ses decimales DECLAREES (toujours ce nombre
 * de chiffres apres la virgule) ou, sans elles, jusqu'a six : une valeur saisie
 * avec trois decimales ne perd pas la troisieme en silence.
 */
export function nombreFrancais(valeur: number, decimales?: number): string {
  return nombreEnTexte(valeur, 'fr-FR', decimales === undefined
    ? { maximumFractionDigits: 6 }
    : { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}

/**
 * LE FORMAT D'UN NOMBRE DE COLONNE : la regle du data-model (`formatDuNombre`),
 * lue sur le type declare et les reglages de la colonne, son habillage compris
 * (`ReglagesDeColonne.habillage`). `montant`, `pourcentage`, `duree`, `taux`, ou
 * `null` pour un nombre sans format et pour tout ce qui n'est pas un nombre.
 */
export function formatDeColonne(type: TypeDeChamp | undefined, reglages: ReglagesDeColonne | undefined): FormatDeNombre | null {
  return formatDuNombre(champAFormat(type, reglages), reglages?.habillage);
}

/** Ce que la regle du format lit d'une colonne : son type et ses deux reglages de format. */
function champAFormat(type: TypeDeChamp | undefined, reglages: ReglagesDeColonne | undefined): ChampAFormat {
  return {
    ...(type === undefined ? {} : { type }),
    ...(reglages?.devise === undefined ? {} : { devise: reglages.devise }),
    ...(reglages?.affichage === undefined ? {} : { affichage: reglages.affichage }),
  };
}

/** UN NOMBRE MONTRE EN JAUGE : un `number` qui declare `affichage: 'jauge'`, de 0 a 100. */
export function enJauge(type: TypeDeChamp | undefined, reglages: ReglagesDeColonne | undefined): boolean {
  return type === 'number' && reglages?.affichage === 'jauge';
}

/** La devise d'un montant de colonne (`deviseDe` du data-model), `EUR` par defaut. */
export const deviseDeColonne = (type: TypeDeChamp | undefined, reglages: ReglagesDeColonne | undefined): string =>
  deviseDuNombre(champAFormat(type, reglages), reglages?.habillage) ?? DEVISE_PAR_DEFAUT;

/**
 * LE SYMBOLE D'UNE DEVISE (« € », « $ », « CHF »), celui que le navigateur
 * ecrit dans un montant : c'est le suffixe de la case de saisie, qui doit
 * montrer le meme signe que la case en lecture.
 */
export function symboleDeDevise(devise: string): string {
  try {
    const parties = formatIntlDeNombre('fr-FR', { style: 'currency', currency: devise }).formatToParts(0);
    return parties.find((partie) => partie.type === 'currency')?.value ?? devise;
  } catch {
    return devise;
  }
}

/** La note maximale d'un champ `note` (5 par defaut). */
export const maxDeNote = (reglages: ReglagesDeColonne | undefined): number => reglages?.max ?? NOTE_MAX_PAR_DEFAUT;

/* ------------------------------------------------------------------ */
/* Le texte d'une valeur, par type                                      */
/* ------------------------------------------------------------------ */

/** Le jour d'une date pure, a la francaise (`05/11/2026`). */
const jour = (texte: string): string => datePure(texte) || texte;

/** UNE PERIODE LUE A LA FRANCAISE : « 05/11/2026 → 04/11/2027 ». Vide si elle est illisible. */
export function texteDePeriode(valeur: Cell | undefined): string {
  const periode = lirePeriode(valeur);
  return periode === null ? '' : `${jour(periode.debut)} → ${jour(periode.fin)}`;
}

/** Sa duree en mots (« 1 an », « 12 jours »), ou rien. */
export function dureeDePeriode(valeur: Cell | undefined): string {
  return libelleDureeDePeriode(typeof valeur === 'string' ? valeur : '') ?? '';
}

/** OU EN EST UNE LISTE DE CONTROLE, en mots : « 2 sur 3 ». Vide sans element. */
export function texteDAvancement(valeur: Cell | undefined): string {
  const { faits, total } = avancementChecklist(lignesDeCellule(valeur));
  return total === 0 ? '' : `${faits} sur ${total}`;
}

/**
 * LE TEXTE D'UN NOMBRE DE CE TYPE, reglages compris. C'est l'habillage d'une
 * case comme celui d'un TOTAL (le pied d'un tableau, une stat) : « 240 m² »
 * et « 1 250 m² » sortent de la meme fonction. Un `number` sans unite ni
 * decimales s'ecrit lui aussi a la francaise (« 1,2 », jamais « 1.2 ») ; la
 * valeur stockee, le tri et l'export restent le nombre. `undefined` pour un
 * type qui n'est pas un nombre.
 *
 * LE FORMAT EST CELUI DU DATA-MODEL (`formatDuNombre`), jamais un second : un
 * `number` ou un `calcul` qui porte une devise s'ecrit en montant, une jauge
 * et un habillage `pourcentage` en « 65 % », un habillage `duree` en
 * « 2 h 30 », un habillage `taux` (une fraction : 0,21) en « 21 % », un
 * habillage `euros` en euros.
 */
export function formatDeNombre(
  type: TypeDeChamp | undefined,
  reglages: ReglagesDeColonne | undefined,
): ((valeur: Cell) => string) | undefined {
  const avecUnite = (texte: string): string => (reglages?.unite === undefined || texte === '' ? texte : `${texte} ${reglages.unite}`);
  const nombre = (valeur: Cell): string =>
    typeof valeur === 'number' && Number.isFinite(valeur) ? nombreFrancais(valeur, reglages?.decimales) : '';
  switch (type) {
    case 'number':
    case 'calcul':
      /* UN CALCUL EN DEVISE S'ECRIT COMME UN MONTANT (la valeur stockee est
         deja arrondie a ses decimales par l'evaluateur, `decimalesDuCalcul`) ;
         en unite, comme un nombre avec unite. */
      switch (formatDeColonne(type, reglages)) {
        case 'montant': {
          const devise = deviseDeColonne(type, reglages);
          const decimales = reglages?.decimales;
          return (valeur) => montant(valeur, devise, decimales);
        }
        case 'pourcentage':
          return pourcentage;
        case 'duree':
          return duree;
        case 'taux':
          return taux;
        default:
          return (valeur) => avecUnite(nombre(valeur));
      }
    case 'note':
      return (valeur) => (typeof valeur === 'number' && Number.isFinite(valeur)
        ? `${nombreFrancais(valeur, Number.isInteger(valeur) ? 0 : 1)} / ${maxDeNote(reglages)}`
        : '');
    default:
      return undefined;
  }
}

/**
 * LE TEXTE D'UNE VALEUR, PAR TYPE : ce qu'une case dit quand on ne peut lui
 * donner qu'une chaine (le libelle d'un lot a confirmer, une recherche, une
 * cellule de tableau en texte). Les nombres passent par `formatDeNombre`, les
 * autres types ont leur mot ; `undefined` quand le type n'en a pas a lui.
 *
 * SES REGLAGES SONT CEUX DU CHAMP DECLARE (`devise`, `affichage`, `plusieurs`,
 * `unite`, `decimales`, `max`), plus l'`habillage` de la colonne quand
 * l'appelant l'a (la case, le PDF) : le fil de suivi d'une fiche, cote
 * serveur, n'a que le champ, et un nombre habille `duree` s'y ecrit nu.
 */
export function texteDeValeur(
  type: TypeDeChamp | undefined,
  valeur: Cell | undefined,
  reglages?: ReglagesDeColonne,
  /**
   * LE CATALOGUE D'UNE LISTE, pour qui n'a que la valeur stockee (le fil de
   * suivi d'une fiche, cote serveur) : la clef devient son libelle. L'ecran,
   * lui, a deja le libelle par la projection et ne le passe pas.
   */
  options?: readonly { value: string; label: string }[],
): string | undefined {
  if (valeur === undefined) return undefined;
  const nombre = formatDeNombre(type, reglages);
  if (nombre !== undefined) return nombre(valeur);
  const libelle = (clef: string): string => options?.find((option) => option.value === clef)?.label ?? clef;
  switch (type) {
    case 'select':
      return options === undefined || valeur === null || valeur === '' ? undefined : libelle(String(valeur));
    case 'multiselect':
      return options === undefined ? undefined : lignesDeCellule(valeur).map(libelle).join(', ');
    case 'lien': {
      /* DES LIENS MULTIPLES : des ids que seul l'annuaire nomme, donc sans lui
         on dit combien. Un lien seul n'a pas de mot a lui (son nom vient de
         l'annuaire, ou c'est l'id). */
      if (!liensMultiples(type, reglages)) return undefined;
      const n = lignesDeCellule(valeur).length;
      return n === 0 ? '' : n === 1 ? '1 fiche' : `${n} fiches`;
    }
    case 'numero':
      return valeur === null ? '' : String(valeur);
    case 'boolean': {
      const lu = lireBooleen(valeur);
      return lu === null ? '' : lu ? 'Oui' : 'Non';
    }
    case 'periode': {
      /* Comme la case : les deux jours, puis la duree (« · 1 an »). */
      const texte = texteDePeriode(valeur);
      const longueur = dureeDePeriode(valeur);
      return texte === '' || longueur === '' ? texte : `${texte} · ${longueur}`;
    }
    case 'checklist':
      return texteDAvancement(valeur);
    case 'etiquettes':
      return lignesDeCellule(valeur).join(', ');
    case 'datetime':
      return texteDeDateHeure(valeur, reglages?.fuseau) ?? undefined;
    default:
      return undefined;
  }
}

/* ------------------------------------------------------------------ */
/* La date-heure                                                        */
/* ------------------------------------------------------------------ */

/**
 * UNE DATE-HEURE A LA FRANCAISE, dans le fuseau de l'org (`16/10/2026 10:10`) :
 * le jour ET l'heure, parce qu'un instant sans son heure n'est pas un instant.
 * Le fuseau est celui du reglage de l'org (`docs/socle/fuseau-org.md`), sinon
 * Europe/Luxembourg ; il n'est jamais celui du poste, pour que deux personnes
 * lisent la meme heure. `null` quand la valeur n'est pas un instant : une
 * `Date` valide, ou une chaine ISO complete du sac (`instantDeSac`).
 */
export function texteDeDateHeure(valeur: Cell | undefined, fuseau?: string): string | null {
  const instant = valeur instanceof Date ? (Number.isNaN(valeur.getTime()) ? null : valeur) : instantDeSac(valeur);
  if (instant === null) return null;
  return formatDeDate('fr-FR', { dateStyle: 'short', timeStyle: 'short', timeZone: fuseauDe(fuseau) }).format(instant);
}

/**
 * L'HEURE D'UNE CARTE (bas de carte de kanban), dans le fuseau de l'org : « 09:30 »
 * quand l'instant tombe AUJOURD'HUI, « 28/10 09:30 » un autre jour de la meme
 * annee, « 28/10/2025 09:30 » une autre annee (sans l'annee, une carte d'une
 * autre annee serait ambigue). Le jour et l'annee se comparent DANS le fuseau
 * (`jourDansFuseau`), jamais dans celui du poste : un instant a 23:30 UTC est
 * deja le lendemain a Luxembourg. `maintenant` est un parametre pour que le
 * calcul reste pur. Docs : `docs/socle/carte-heure.md`.
 */
export function texteDHeureDeCarte(instant: Date, fuseau: string | undefined, maintenant: Date): string {
  const zone = fuseauDe(fuseau);
  const heure = formatDeDate('fr-FR', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(instant);
  const jour = jourDansFuseau(instant, zone);
  const aujourdhui = jourDansFuseau(maintenant, zone);
  if (jour === aujourdhui) return heure;
  const date = formatDeDate('fr-FR', {
    timeZone: zone,
    day: '2-digit',
    month: '2-digit',
    ...(jour.slice(0, 4) === aujourdhui.slice(0, 4) ? {} : { year: 'numeric' as const }),
  }).format(instant);
  return `${date} ${heure}`;
}

/* ------------------------------------------------------------------ */
/* Les totaux                                                           */
/* ------------------------------------------------------------------ */

/**
 * LA COLONNE SE SOMME-T-ELLE AU CENTIME ? La regle du data-model
 * (`seSommeAuCentime`), lue sur le type et les reglages d'une colonne, son
 * habillage compris : une colonne habillee `euros` est un montant. Sans type
 * (une colonne native), l'habillage seul en decide.
 */
export function sommeAuCentimeDe(type: TypeDeChamp | undefined, reglages: ReglagesDeColonne | undefined): boolean {
  return seSommeAuCentime(champAFormat(type, reglages), reglages?.habillage);
}

/** La somme de ces nombres, au centime quand la colonne le veut. */
export function sommer(nombres: readonly number[], auCentime: boolean): number {
  return auCentime ? sommeAuCentime(nombres) : nombres.reduce((total, nombre) => total + nombre, 0);
}

/* ------------------------------------------------------------------ */
/* La saisie                                                            */
/* ------------------------------------------------------------------ */

/** Les editeurs qui saisissent un nombre : `number` (tout format) et la note. */
type EditeurDeNombre = 'number' | 'note';
const saisitUnNombre = (editeur: EditorKind): editeur is EditeurDeNombre => editeur === 'number' || editeur === 'note';

/** La lecture d'une saisie par la porte d'entree du data-model, le format de la colonne compris. */
function lireSaisie(type: EditeurDeNombre, texte: string, reglages: ReglagesDeColonne | undefined) {
  return valeurNormalisee(
    {
      key: '',
      type,
      ...(reglages?.max === undefined ? {} : { max: reglages.max }),
      ...(reglages?.devise === undefined ? {} : { devise: reglages.devise }),
      ...(reglages?.affichage === undefined ? {} : { affichage: reglages.affichage }),
    },
    texte,
    undefined,
    reglages?.habillage,
  );
}

/**
 * UNE SAISIE NUMERIQUE LIBRE, ramenee a la forme stockee par la porte d'entree
 * du data-model (`valeurNormalisee`), SELON LE FORMAT de la colonne : « 1,2 »
 * devient 1.2, un montant « 1 250,50 € » 1250.5, une duree « 2 h 30 » 150
 * minutes, une jauge refuse 120, « 4/5 » la note 4. `null` pour une case
 * videe, `undefined` pour une saisie refusee : elle ne s'ecrit pas, comme un
 * nombre invalide (`fromInput`).
 */
export function saisieNumerique(
  type: EditeurDeNombre,
  brut: string,
  reglages?: ReglagesDeColonne,
): number | null | undefined {
  const texte = brut.trim();
  if (texte === '') return null;
  const lu = lireSaisie(type, texte, reglages);
  return 'valeur' in lu && typeof lu.valeur === 'number' ? lu.valeur : undefined;
}

/**
 * POURQUOI UNE SAISIE NUMERIQUE EST REFUSEE, en une ligne : le motif de
 * `valeurNormalisee` (le meme que le MCP rend a un agent), precede
 * d'« Attendu : ». `null` quand la saisie se lit, ou que le type n'est pas
 * numerique. C'est ce que la case de fiche montre sous le champ au lieu de se
 * refermer en silence.
 */
export function motifDeRefus(type: EditorKind, brut: string, reglages?: ReglagesDeColonne): string | null {
  if (!saisitUnNombre(type)) return null;
  const texte = brut.trim();
  if (texte === '') return null;
  const lu = lireSaisie(type, texte, reglages);
  return 'refus' in lu ? `Attendu : ${lu.refus}.` : null;
}

/** Les deux jours d'une saisie de periode (`debut/fin`), chacun vide s'il manque. */
export function moitiesDePeriode(brouillon: string): { debut: string; fin: string } {
  const [debut = '', fin = ''] = brouillon.split('/');
  return { debut: debut.trim(), fin: fin.trim() };
}

/**
 * UNE SAISIE DE PERIODE ramenee a sa forme stockee : `null` quand les deux
 * jours sont vides, la chaine `debut/fin` quand ils forment une periode,
 * `undefined` sinon (un seul jour, une fin avant le debut) : elle ne s'ecrit
 * pas, et la case garde sa valeur.
 */
export function saisieDePeriode(brouillon: string): Cell | undefined {
  const { debut, fin } = moitiesDePeriode(brouillon);
  if (debut === '' && fin === '') return null;
  const lue = lirePeriode(`${debut}/${fin}`);
  return lue === null ? undefined : `${lue.debut}/${lue.fin}`;
}
